import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LicenseFilters } from '@/components/app/license-filters';
import { LicensesTable } from '@/components/app/licenses-table';
import { CreateLicenseSheet } from '@/routes/licenses/new';
import { apiFetch } from '@/lib/api';
import { useTranslation } from 'react-i18next';
import { licensesListResponseSchema } from '@/schemas/licenses';
import type { SortingState, Updater } from '@tanstack/react-table';

async function fetchLicenses(endpoint: string) {
  const resp = await apiFetch(endpoint);
  const json = await resp.json();
  return licensesListResponseSchema.parse(json);
}

function buildEndpoint(searchParams: URLSearchParams): string {
  const p = new URLSearchParams();
  const page = searchParams.get('page') ?? '1';
  const limit = searchParams.get('limit') ?? '20';
  p.set('page', page);
  p.set('limit', limit);

  const search = searchParams.get('search');
  if (search) p.set('search', search);

  const status = searchParams.get('status');
  if (status) p.set('status', status);

  const type = searchParams.get('type');
  if (type) p.set('type', type);

  const sort = searchParams.get('sort');
  if (sort) p.set('sort', sort);

  // The 2B entity split (#163): the Robot / Software tabs are the section
  // selector, so the kind always travels with the request (default `robot`).
  p.set('kind', searchParams.get('kind') === 'software' ? 'software' : 'robot');

  return `/dashboard/licenses?${p.toString()}`;
}

function TableSkeleton() {
  return (
    <div className="space-y-2 pt-4">
      <div className="flex gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={`h-${i}`} className="h-9 flex-1" />
        ))}
      </div>
      {Array.from({ length: 5 }).map((_, ri) => (
        <div key={ri} className="flex gap-2">
          {Array.from({ length: 6 }).map((_, ci) => (
            <Skeleton key={`${ri}-${ci}`} className="h-11 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function LicensesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  const endpoint = useMemo(() => buildEndpoint(searchParams), [searchParams]);

  const query = useQuery({
    queryKey: ['licenses', 'list', endpoint],
    queryFn: () => fetchLicenses(endpoint),
    staleTime: 30_000,
  });

  const sorting: SortingState = useMemo(() => {
    const sort = searchParams.get('sort');
    if (!sort) return [];
    const [id, desc] = sort.split(':');
    if (!id) return [];
    return [{ id, desc: desc === 'desc' }];
  }, [searchParams]);

  const setSorting = (updater: Updater<SortingState>) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      const first = next[0];
      if (first) {
        p.set('sort', `${first.id}:${first.desc ? 'desc' : 'asc'}`);
      } else {
        p.delete('sort');
      }
      return p;
    });
  };

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10));
  const limit = Math.max(1, parseInt(searchParams.get('limit') ?? '20', 10));

  const setPage = (next: number) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('page', String(next));
      return p;
    });
  };

  const setLimit = (next: number) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('limit', String(next));
      p.set('page', '1');
      return p;
    });
  };

  const data = query.data;
  const isFirstLoad = query.isLoading && !data;
  const isRefetching = query.isFetching && !query.isLoading;
  const total = data?.total ?? 0;
  const pages = data?.pages ?? 1;
  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  const hasFilters =
    (searchParams.get('search') ?? '').length > 0 ||
    (searchParams.get('status') ?? '').length > 0 ||
    (searchParams.get('type') ?? '').length > 0;

  const clearFilters = () => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete('search');
      p.delete('status');
      p.delete('type');
      p.set('page', '1');
      return p;
    });
  };

  return (
    <div className="mx-auto w-full max-w-[1120px]">
      <CreateLicenseSheet
        open={searchParams.get('action') === 'new'}
        onOpenChange={(open) => {
          if (!open) {
            setSearchParams((prev) => {
              const p = new URLSearchParams(prev);
              p.delete('action');
              return p;
            });
          }
        }}
      />

      {/* Page head */}
      <div className="flex items-start justify-between gap-8">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-foreground">
            {t('licenses.title')}
          </h1>
          <p className="mt-2.5 max-w-[52ch] font-serif text-[17.5px] italic leading-relaxed text-muted-foreground">
            {t('licenses.subtitle')}
          </p>
        </div>
        <Button asChild className="mt-2.5 shrink-0">
          <Link to="/licenses/new">{t('licenses.create')}</Link>
        </Button>
      </div>

      {/* Section switch — the 2B split between robot and software licences */}
      <div className="mt-7">
        <KindTabs />
      </div>

      {/* Filters */}
      <div className="mt-5">
        <LicenseFilters />
      </div>

      {/* Content */}
      {query.isError ? (
        <div className="mt-6 flex items-center justify-between rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{t('licenses.listError')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      ) : isFirstLoad ? (
        <TableSkeleton />
      ) : total === 0 ? (
        hasFilters ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <p className="text-[15px] text-muted-foreground">{t('licenses.noMatch')}</p>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              {t('common.clearFilters')}
            </Button>
          </div>
        ) : (
          <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
            <h2 className="font-serif text-2xl italic tracking-tight text-foreground">
              {t('licenses.emptyTitle')}
            </h2>
            <p className="max-w-[42ch] text-center text-[14.5px] text-muted-foreground">
              {t('licenses.emptyHint')}
            </p>
            <Button asChild className="mt-1">
              <Link to="/licenses/new">{t('licenses.emptyCta')}</Link>
            </Button>
          </div>
        )
      ) : (
        <div
          className={`transition-opacity ${isRefetching ? 'pointer-events-none opacity-60' : ''}`}
        >
          <LicensesTable data={data?.data ?? []} sorting={sorting} onSortingChange={setSorting} />
        </div>
      )}

      {/* Pagination */}
      {total > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <span className="text-[13px] tabular-nums text-faint">
            {t('common.showingRange', { start, end, total })}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-input bg-transparent"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              aria-label={t('common.previousPage')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[64px] text-center text-[13px] tabular-nums text-muted-foreground">
              {page} / {pages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 border-input bg-transparent"
              onClick={() => setPage(page + 1)}
              disabled={page >= pages}
              aria-label={t('common.nextPage')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Select value={String(limit)} onValueChange={(v) => setLimit(parseInt(v, 10))}>
              <SelectTrigger
                className="h-8 w-[72px] border-input bg-transparent text-[13px]"
                aria-label={t('common.pageSize')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
    </div>
  );
}

export const handle = { crumb: 'Licenses' };

/**
 * The Robot | Software section switch (#163). Robot licences and software keys are
 * different entities, so the list is split by serial shape (the server-side `kind`
 * filter). The choice lives in the URL, so a tab is shareable and survives reloads.
 */
function KindTabs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const active = searchParams.get('kind') === 'software' ? 'software' : 'robot';

  const select = (kind: 'robot' | 'software') =>
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('kind', kind);
      p.set('page', '1');
      return p;
    });

  const tabs = [
    { kind: 'robot' as const, labelKey: 'licenses.kind.robot' },
    { kind: 'software' as const, labelKey: 'licenses.kind.software' },
  ];

  return (
    <div
      className="inline-flex rounded-lg border border-border bg-secondary/40 p-0.5"
      role="tablist"
      aria-label={t('licenses.kind.label')}
    >
      {tabs.map((tab) => (
        <button
          key={tab.kind}
          role="tab"
          type="button"
          aria-selected={active === tab.kind}
          data-testid={`kind-tab-${tab.kind}`}
          className={`rounded-md px-3.5 py-1.5 text-[13.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            active === tab.kind
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => select(tab.kind)}
        >
          {t(tab.labelKey)}
        </button>
      ))}
    </div>
  );
}
